import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container, SectionTitle, SectionSubtitle } from '../../styles/Layout';
import { 
  FaNodeJs, 
  FaReact, 
  FaDatabase, 
  FaCloud, 
  FaCubes, 
  FaTools,
  FaRobot,
  FaMobile,
  FaShieldAlt
} from 'react-icons/fa';

const TechSection = styled.section`
  padding: var(--spacing-xxxl) 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 25% 25%, rgba(255, 0, 128, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(0, 212, 255, 0.1) 0%, transparent 50%);
    z-index: 1;
  }
`;

const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--spacing-xl);
  margin-top: var(--spacing-xl);
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }
`;

const TechCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: var(--spacing-xl);
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(135deg, ${props => props.color || 'var(--neon-blue)'} 0%, transparent 100%);
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%);
    transform: translateY(-10px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    border-color: ${props => props.color || 'var(--neon-blue)'};
    
    .tech-icon {
      transform: scale(1.1) rotate(5deg);
      text-shadow: 0 0 20px currentColor;
    }
  }
`;

const TechIcon = styled.div`
  font-size: 3rem;
  margin-bottom: var(--spacing-lg);
  color: ${props => props.color || 'var(--neon-blue)'};
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);
  display: inline-block;
`;

const TechTitle = styled.h3`
  font-size: 1.6rem;
  margin-bottom: var(--spacing-lg);
  background: linear-gradient(135deg, var(--text-primary) 0%, ${props => props.color || 'var(--neon-blue)'} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const TechList = styled.ul`
  list-style-type: none;
  
  li {
    margin-bottom: var(--spacing-md);
    position: relative;
    padding-left: 25px;
    color: var(--text-secondary);
    line-height: 1.6;
    transition: all 0.3s ease;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 10px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: ${props => props.color || 'var(--neon-blue)'};
      box-shadow: 0 0 10px ${props => props.color || 'var(--neon-blue)'};
    }
    
    &:hover {
      color: var(--text-primary);
      padding-left: 30px;
    }
  }
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  font-size: 1.5rem;
  color: ${props => props.color || 'var(--neon-blue)'};
  opacity: 0.1;
  pointer-events: none;
  z-index: 1;
`;

const TechStack = () => {
  const techCategories = [
    {
      icon: <FaRobot />,
      title: "AI & Machine Learning",
      color: "var(--neon-green)",
      items: [
        "OpenAI GPT-4 Integration",
        "TensorFlow & PyTorch",
        "Computer Vision APIs",
        "Natural Language Processing",
        "Predictive Analytics",
        "AI-Powered Automation"
      ]
    },
    {
      icon: <FaReact />,
      title: "Frontend Development",
      color: "var(--neon-blue)",
      items: [
        "React.js & Next.js",
        "Vue.js & Nuxt.js",
        "TypeScript",
        "Tailwind CSS",
        "Framer Motion",
        "Progressive Web Apps"
      ]
    },
    {
      icon: <FaNodeJs />,
      title: "Backend Development",
      color: "var(--neon-green)",
      items: [
        "Node.js & Express",
        "Python & Django",
        "GraphQL & REST APIs",
        "Microservices Architecture",
        "Real-time WebSockets",
        "Serverless Functions"
      ]
    },
    {
      icon: <FaDatabase />,
      title: "Database & Storage",
      color: "var(--neon-purple)",
      items: [
        "PostgreSQL & MongoDB",
        "Redis & Elasticsearch",
        "Firebase & Supabase",
        "Data Modeling",
        "Performance Optimization",
        "Backup & Recovery"
      ]
    },
    {
      icon: <FaCloud />,
      title: "Cloud & DevOps",
      color: "var(--neon-blue)",
      items: [
        "Google Cloud Platform",
        "AWS & Azure",
        "Docker & Kubernetes",
        "CI/CD Pipelines",
        "Infrastructure as Code",
        "Monitoring & Logging"
      ]
    },
    {
      icon: <FaCubes />,
      title: "Blockchain & Web3",
      color: "var(--neon-magenta)",
      items: [
        "Solana Development",
        "Smart Contracts",
        "Web3.js & Ethers.js",
        "DeFi Protocols",
        "NFT Marketplaces",
        "Crypto Wallets"
      ]
    },
    {
      icon: <FaMobile />,
      title: "Mobile Development",
      color: "var(--neon-yellow)",
      items: [
        "React Native",
        "Flutter",
        "iOS & Android Native",
        "Cross-platform Solutions",
        "App Store Optimization",
        "Push Notifications"
      ]
    },
    {
      icon: <FaShieldAlt />,
      title: "Security & Testing",
      color: "var(--neon-magenta)",
      items: [
        "OAuth & JWT",
        "Penetration Testing",
        "Automated Testing",
        "Code Quality Assurance",
        "Security Audits",
        "Compliance Standards"
      ]
    },
    {
      icon: <FaTools />,
      title: "Development Tools",
      color: "var(--neon-yellow)",
      items: [
        "Git & GitHub",
        "VS Code & JetBrains",
        "Figma & Adobe Creative",
        "Postman & Insomnia",
        "Jira & Linear",
        "Slack & Discord"
      ]
    }
  ];

  const floatingElements = [
    { icon: '⚡', top: '10%', left: '5%', color: 'var(--neon-yellow)' },
    { icon: '🚀', top: '20%', right: '10%', color: 'var(--neon-blue)' },
    { icon: '💎', bottom: '30%', left: '8%', color: 'var(--neon-magenta)' },
    { icon: '🔮', bottom: '15%', right: '5%', color: 'var(--neon-purple)' },
    { icon: '⭐', top: '60%', left: '3%', color: 'var(--neon-green)' },
  ];

  return (
    <TechSection id="tech">
      {floatingElements.map((element, index) => (
        <FloatingElement
          key={index}
          color={element.color}
          style={{ 
            top: element.top, 
            left: element.left, 
            right: element.right, 
            bottom: element.bottom 
          }}
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 4 + index,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {element.icon}
        </FloatingElement>
      ))}
      
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <SectionTitle>
            <img 
              src={require('../../assets/logos/Labs_white.png')} 
              alt="GeoX Labs Logo" 
              style={{height: '50px', verticalAlign: 'middle', marginRight: '15px'}} 
            /> 
            Technology Stack
          </SectionTitle>
          <SectionSubtitle>
            We leverage cutting-edge technologies and integrate AI tools in every phase of development 
            to build scalable, secure, and high-performance solutions with premium design aesthetics.
          </SectionSubtitle>
        </motion.div>
        
        <TechGrid>
          {techCategories.map((category, index) => (
            <TechCard
              key={index}
              color={category.color}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <TechIcon color={category.color} className="tech-icon">
                {category.icon}
              </TechIcon>
              <TechTitle color={category.color}>{category.title}</TechTitle>
              <TechList color={category.color}>
                {category.items.map((item, itemIndex) => (
                  <motion.li 
                    key={itemIndex}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: (index * 0.1) + (itemIndex * 0.05) }}
                  >
                    {item}
                  </motion.li>
                ))}
              </TechList>
            </TechCard>
          ))}
        </TechGrid>
      </Container>
    </TechSection>
  );
};

export default TechStack;